import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PlaceTypeDropdown from '../common/PlaceTypeDropdown';
import * as AreaUtils from '../../utils/searchAreaUtils';

const SearchSidebar = ({
  searchMode: shapeSearchMode,
  setSearchMode: setShapeSearchMode,
  handleShapeDelete,
  currentShape,
  shapeInteractionMode,
  setShapeInteractionMode,
  setIsDrawingMode,
  shapeSize,
  setShapeSize,
  tubeThickness,
  setTubeThickness,
  undoShapeChange,
  historyIndex,
  isGeneratingCircles,
  selectedPolygonCircles,
  currentRadius,
  handleRadiusChange,
  selectedArea,
  deleteSelectedArea,
  addAdjacentArea,
  includedTypes,
  excludedTypes,
  addIncludedType,
  removeIncludedType,
  addExcludedType,
  removeExcludedType,
  formatPlaceTypeDisplay,
  handlePolygonSearch,
  handleSearch,
  isSearching,
  unsearchedAreas,
  getAreaLimit,
  getRemainingRequests,
  getRequestLimit,
  getUserType,
  showCustomPolygonRadiusInput,
  setShowCustomPolygonRadiusInput,
  customPolygonRadiusInput,
  handleCustomPolygonRadiusSubmit,
  polygonRadius,
  handlePolygonRadiusChange,
  handleGenerateShapeCircles,
  generatedCircles,
  selectAllPolygonCircles,
  deselectAllPolygonCircles,
  searchedAreas,
  visibleAreas,
  deleteLatestUnsearchedArea,
  clearUnsearchedAreas,
  deleteLatestSearchedArea,
  deleteAllSearchedAreas,
  showCustomRadiusInput,
  customRadiusInput,
  setCustomRadiusInput,
  handleCustomRadiusSubmit,
  setShowCustomRadiusInput,
  setCustomPolygonRadiusInput,
  // TEXT SEARCH PROPS
  filterSearchMode,
  setFilterSearchMode,
  textQuery,
  setTextQuery
}) => {
  const [expandedSections, setExpandedSections] = useState({
    shapeControls: true,
    areaManagement: true,
    filters: true
  });

  useEffect(() => {
    if (generatedCircles.length > 0 && selectedPolygonCircles.size === 0) {
      selectAllPolygonCircles();
    }
  }, [generatedCircles.length]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="w-80 bg-[#f5f5f5] border-r border-gray-300 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-3 py-3 bg-white border-b border-gray-300">
        <div className="flex items-center justify-between mb-2.5">
          <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Search Areas</h2>
        </div>
        
        {/* Mode Toggle - Figma style segmented control */}
        <div className="flex bg-white border border-gray-300 rounded-md p-0.5">
          <button
            onClick={() => {
              setShapeSearchMode('circles');
              handleShapeDelete();
            }}
            className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all ${
              shapeSearchMode === 'circles'
                ? 'bg-blue-500 text-white shadow-sm' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            ⭕ Circles
          </button>
          <button
            onClick={() => {
              handleShapeDelete();
              setShapeSearchMode('polygon');
              setIsDrawingMode(true);
              toast('Click on map to draw pentagon', {
                icon: 'ℹ️',
                duration: 2000
              });
            }}
            className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all ${
              shapeSearchMode === 'polygon'
                ? 'bg-blue-500 text-white shadow-sm' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            ⬟ Pentagon
          </button>
          <button
            onClick={() => {
              handleShapeDelete();
              setShapeSearchMode('tube');
              setIsDrawingMode(true);
              toast('Click on map to draw tube', {
                icon: 'ℹ️',
                duration: 2000
              });
            }}
            className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all ${
              shapeSearchMode === 'tube'
                ? 'bg-blue-500 text-white shadow-sm' 
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            ⭘ Tube
          </button>
        </div>
      </div>

      {/* Shape Interaction Mode Banner */}
      {(shapeSearchMode === 'polygon' || shapeSearchMode === 'tube') && currentShape && (
        <div className="px-3 py-2 bg-blue-500 border-b border-blue-600">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-white">
              {shapeInteractionMode === 'move' ? '🔄 Move Mode' : '✏️ Reshape Mode'}
            </span>
            <button
              onClick={() => {
                setShapeInteractionMode(prev => prev === 'move' ? 'reshape' : 'move');
                toast(`${shapeInteractionMode === 'move' ? 'Reshape' : 'Move'} mode`, {
                  icon: 'ℹ️',
                  duration: 1500
                });
              }}
              className="px-2 py-1 text-xs bg-white/20 hover:bg-white/30 text-white rounded font-medium"
            >
              Toggle (M)
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {/* Polygon/Tube Mode Controls */}
        {(shapeSearchMode === 'polygon' || shapeSearchMode === 'tube') && (
          <div className="bg-white border-b border-gray-300">
            <button
              onClick={() => toggleSection('shapeControls')}
              className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                {shapeSearchMode === 'polygon' ? 'Pentagon' : 'Tube'} Controls
              </span>
              <span className="text-gray-400 text-sm">
                {expandedSections.shapeControls ? '−' : '+'}
              </span>
            </button>
            
            {expandedSections.shapeControls && (
              <div className="px-3 pb-3 space-y-2.5">
                {!currentShape ? (
                  null
                ) : (
                  <div className="space-y-2.5">
                    {/* Keyboard shortcuts info */}
                    <div className="bg-gray-50 border border-gray-200 rounded p-2.5">
                      <div className="text-xs text-gray-600 space-y-1">
                        <div className="flex items-center justify-between">
                          <span>Toggle mode</span>
                          <kbd className="px-2 py-0.5 bg-white border border-gray-300 rounded text-[11px] font-mono">M</kbd>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Undo changes</span>
                          <kbd className="px-2 py-0.5 bg-white border border-gray-300 rounded text-[11px] font-mono">Ctrl+Z</kbd>
                        </div>
                      </div>
                    </div>

                    {/* Size Control */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-medium text-gray-700">
                          {shapeSearchMode === 'polygon' ? 'Pentagon' : 'Tube'} Size
                        </label>
                        <span className="text-xs text-gray-500">{(shapeSize * 50).toFixed(1)}km</span>
                      </div>
                      <input
                        type="range"
                        min="0.005"
                        max="0.3"
                        step="0.005"
                        value={shapeSize}
                        onChange={(e) => setShapeSize(parseFloat(e.target.value))}
                        className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                      />
                    </div>

                    {shapeSearchMode === 'tube' && (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs font-medium text-gray-700">Thickness</label>
                          <span className="text-xs text-gray-500">{(tubeThickness * 50).toFixed(1)}km</span>
                        </div>
                        <input
                          type="range"
                          min="0.005"
                          max="0.05"
                          step="0.005"
                          value={tubeThickness}
                          onChange={(e) => setTubeThickness(parseFloat(e.target.value))}
                          className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                        />
                      </div>
                    )}

                    {/* Circle Radius */}
                    {showCustomPolygonRadiusInput ? (
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">Custom Radius (m)</label>
                        <div className="flex gap-1.5">
                          <input
                            type="number"
                            value={customPolygonRadiusInput}
                            onChange={(e) => setCustomPolygonRadiusInput(e.target.value)}
                            placeholder="Enter radius"
                            className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                            min="500"
                            max="50000"
                          />
                          <button
                            onClick={handleCustomPolygonRadiusSubmit}
                            className="px-2.5 py-1.5 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                          >
                            Set
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            setShowCustomPolygonRadiusInput(false);
                            setCustomPolygonRadiusInput('');
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs font-medium text-gray-700">Circle Radius</label>
                          <button
                            onClick={() => setShowCustomPolygonRadiusInput(true)}
                            className="text-xs text-blue-500 hover:text-blue-600 font-medium"
                          >
                            Custom
                          </button>
                        </div>
                        <input
                          type="range"
                          min="500"
                          max="50000"
                          step="500"
                          value={polygonRadius}
                          onChange={(e) => handlePolygonRadiusChange(e.target.value)}
                          className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500 mb-1"
                        />
                        <div className="text-xs text-gray-500 text-center">{polygonRadius}m</div>
                      </div>
                    )}

                    {/* Generate Circles Button */}
                    <button
                      onClick={handleGenerateShapeCircles}
                      disabled={isGeneratingCircles}
                      className="w-full px-3 py-2 bg-purple-500 text-white rounded text-xs font-medium hover:bg-purple-600 disabled:bg-gray-300 transition-colors"
                    >
                      {isGeneratingCircles ? '⟳ Generating...' : '⊕ Generate Circles'}
                    </button>

                    {/* Circle Selection */}
                    {generatedCircles.length > 0 && (
                      <div className="bg-gray-50 border border-gray-200 rounded p-2.5 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-700">
                            {selectedPolygonCircles.size} / {generatedCircles.length} circles
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={selectAllPolygonCircles}
                              className="text-xs text-blue-500 hover:text-blue-600 font-medium"
                            >
                              Select All
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={deselectAllPolygonCircles}
                              className="text-xs text-blue-500 hover:text-blue-600 font-medium"
                            >
                              None
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Delete Shape Button */}
                    <button
                      onClick={handleShapeDelete}
                      className="w-full px-3 py-2 bg-red-50 border border-red-200 text-red-600 rounded text-xs font-medium hover:bg-red-100 transition-colors"
                    >
                      🗑 Delete Shape
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Circle Mode Radius Control */}
        {shapeSearchMode === 'circles' && (
          <div className="bg-white border-b border-gray-300 px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Radius</label>
              <button
                onClick={() => setShowCustomRadiusInput(!showCustomRadiusInput)}
                className="text-xs text-blue-500 hover:text-blue-600 font-medium"
              >
                {showCustomRadiusInput ? 'Slider' : 'Custom'}
              </button>
            </div>

            {showCustomRadiusInput ? (
              <div className="space-y-1.5">
                <div className="flex gap-1.5">
                  <input
                    type="number"
                    value={customRadiusInput}
                    onChange={(e) => setCustomRadiusInput(e.target.value)}
                    placeholder="500-50000"
                    className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                    min="500"
                    max="50000"
                  />
                  <button
                    onClick={handleCustomRadiusSubmit}
                    className="px-2.5 py-1.5 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                  >
                    Set
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowCustomRadiusInput(false);
                    setCustomRadiusInput('');
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <input
                  type="range"
                  min="500"
                  max="50000"
                  step="500"
                  value={currentRadius}
                  onChange={(e) => handleRadiusChange(e.target.value)}
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500 mb-1.5"
                />
                <div className="text-xs text-gray-500 text-center font-medium">{currentRadius}m</div>
              </>
            )}
          </div>
        )}

        {/* Selected Area Info */}
        {selectedArea && (
          <div className="bg-white border-b border-gray-300">
            <div className="px-3 py-2.5 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-sm">📍</span>
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Selected Area</h3>
              </div>
              <div className="flex flex-col items-start space-y-1 text-xs text-gray-600">
                <div>{parseFloat(selectedArea.latitude).toFixed(6)}, {parseFloat(selectedArea.longitude).toFixed(6)}</div>
                <div className="flex items-center gap-2">
                  <span>Radius: {selectedArea.radius}m</span>
                  <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                    selectedArea.has_been_searched 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {selectedArea.has_been_searched ? 'Searched' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Adjacent Area Controls */}
            {!selectedArea.has_been_searched && (
              <div className="px-3 py-2.5 border-b border-gray-200">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Add Adjacent</h3>
                <div className="grid grid-cols-3 gap-1.5">
                  <button onClick={() => addAdjacentArea('northwest')} className="p-2 text-sm bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 rounded transition-colors">↖</button>
                  <button onClick={() => addAdjacentArea('north')} className="p-2 text-sm bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 rounded transition-colors">↑</button>
                  <button onClick={() => addAdjacentArea('northeast')} className="p-2 text-sm bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 rounded transition-colors">↗</button>
                  <button onClick={() => addAdjacentArea('west')} className="p-2 text-sm bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 rounded transition-colors">←</button>
                  <div className="p-2 text-sm bg-white border border-gray-200 text-center rounded">•</div>
                  <button onClick={() => addAdjacentArea('east')} className="p-2 text-sm bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 rounded transition-colors">→</button>
                  <button onClick={() => addAdjacentArea('southwest')} className="p-2 text-sm bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 rounded transition-colors">↙</button>
                  <button onClick={() => addAdjacentArea('south')} className="p-2 text-sm bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 rounded transition-colors">↓</button>
                  <button onClick={() => addAdjacentArea('southeast')} className="p-2 text-sm bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 rounded transition-colors">↘</button>
                </div>
              </div>
            )}
            
            <div className="px-3 py-2.5">
              <button
                onClick={deleteSelectedArea}
                className="w-full px-2.5 py-2 bg-red-50 border border-red-200 text-red-600 rounded text-xs font-medium hover:bg-red-100 transition-colors"
              >
                🗑 Delete Area
              </button>
            </div>
          </div>
        )}

        {/* Area Management */}
        <div className="bg-white border-b border-gray-300">
          <button
            onClick={() => toggleSection('areaManagement')}
            className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Area Management</span>
            <span className="text-gray-400 text-sm">
              {expandedSections.areaManagement ? '−' : '+'}
            </span>
          </button>
          
          {expandedSections.areaManagement && (
            <div className="px-3 pb-3 space-y-2.5">
              <div className="text-xs text-gray-500 space-y-1">
                <div className="flex items-center justify-between">
                  <span>Searched</span>
                  <span className="font-medium text-gray-700">{searchedAreas.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Pending</span>
                  <span className="font-medium text-gray-700">{unsearchedAreas.length}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-1.5">
                <div className="relative group">
                  <button
                    onClick={deleteLatestUnsearchedArea}
                    disabled={unsearchedAreas.length === 0}
                    className="w-full px-2 py-2 bg-orange-50 border border-orange-200 text-orange-700 rounded-sm text-xs font-medium hover:bg-orange-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Remove Latest
                  </button>
                  <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-gray-900 text-white text-[11px] rounded-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[9999] pointer-events-none">
                    Delete most recent pending area
                  </div>
                </div>
                
                <div className="relative group">
                  <button
                    onClick={clearUnsearchedAreas}
                    disabled={unsearchedAreas.length === 0}
                    className="w-full px-2 py-2 bg-red-50 border border-red-200 text-red-600 rounded-sm text-xs font-medium hover:bg-red-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Clear Pending
                  </button>
                  <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-gray-900 text-white text-[11px] rounded-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[9999] pointer-events-none">
                    Remove all pending areas
                  </div>
                </div>
                
                <div className="relative group">
                  <button
                    onClick={deleteLatestSearchedArea}
                    disabled={searchedAreas.length === 0}
                    className="w-full px-2 py-2 bg-purple-50 border border-purple-200 text-purple-600 rounded-sm text-xs font-medium hover:bg-purple-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Remove Last
                  </button>
                  <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-gray-900 text-white text-[11px] rounded-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[9999] pointer-events-none">
                    Delete last searched area
                  </div>
                </div>
                
                <div className="relative group">
                  <button
                    onClick={deleteAllSearchedAreas}
                    disabled={searchedAreas.length === 0}
                    className="w-full px-2 py-2 bg-red-50 border border-red-200 text-red-600 rounded-sm text-xs font-medium hover:bg-red-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Clear History
                  </button>
                  <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-gray-900 text-white text-[11px] rounded-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[9999] pointer-events-none">
                    Remove all searched areas
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search Filters */}
        <div className="bg-white border-b border-gray-300">
          <button
            onClick={() => toggleSection('filters')}
            className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Filters</span>
            <span className="text-gray-400 text-sm">
              {expandedSections.filters ? '−' : '+'}
            </span>
          </button>
          
          {expandedSections.filters && (
            <div className="px-3 pb-3 space-y-3">
              {/* Search Mode Toggle */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-blue-900">Search Mode</span>
                </div>
                <div className="flex bg-white border border-gray-300 rounded-md p-0.5">
                  <button
                    onClick={() => AreaUtils.updateFilterSearchMode('category', setFilterSearchMode)}
                    className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all ${
                      filterSearchMode === 'category'
                        ? 'bg-blue-500 text-white shadow-sm' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    📁 Category
                  </button>
                  <button
                    onClick={() => AreaUtils.updateFilterSearchMode('text', setFilterSearchMode)}
                    className={`flex-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all ${
                      filterSearchMode === 'text'
                        ? 'bg-blue-500 text-white shadow-sm' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    📝 Text
                  </button>
                </div>
                {filterSearchMode === 'category' && (
                  <div className="mt-2 text-[11px] text-blue-700 bg-blue-100 rounded px-2 py-1.5">
                    💡 Can't find category? <button 
                      onClick={() => AreaUtils.updateFilterSearchMode('text', setFilterSearchMode)}
                      className="underline font-medium hover:text-blue-800"
                    >
                      Switch to text search
                    </button>
                  </div>
                )}
              </div>

              {/* Text Search Input */}
              {filterSearchMode === 'text' && (
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1.5 block">Text Query</label>
                  <input
                    type="text"
                    value={textQuery}
                    onChange={(e) => AreaUtils.updateTextQuery(e.target.value, setTextQuery)}
                    placeholder="e.g., aesthetic clinic, academy..."
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <div className="mt-1.5 text-[11px] text-gray-500">
                    Enter free-form text to search for businesses
                  </div>
                </div>
              )}

              {/* Include Types */}
              {filterSearchMode === 'category' && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-green-600 text-sm">✓</span>
                    <h3 className="text-xs font-medium text-gray-700">INCLUDE</h3>
                    {includedTypes.length > 0 && (
                      <span className="text-[11px] text-gray-500">({includedTypes.length})</span>
                    )}
                  </div>
                  
                  {includedTypes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {includedTypes.map((type, index) => (
                        <span 
                          key={index} 
                          className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 text-green-700 text-xs rounded hover:bg-green-100 transition-colors"
                        >
                          {formatPlaceTypeDisplay(type)}
                          <button
                            onClick={() => removeIncludedType(index)}
                            className="text-green-600 hover:text-green-800 font-bold leading-none"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <PlaceTypeDropdown
                    placeholder="Add type..."
                    onAdd={addIncludedType}
                    existingTypes={includedTypes}
                  />
                </div>
              )}

              {/* Exclude Types - Only for category search */}
              {filterSearchMode === 'category' && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-red-600 text-sm">✕</span>
                    <h3 className="text-xs font-medium text-gray-700">EXCLUDE</h3>
                    {excludedTypes.length > 0 && (
                      <span className="text-[11px] text-gray-500">({excludedTypes.length})</span>
                    )}
                  </div>
                  
                  {excludedTypes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {excludedTypes.map((type, index) => (
                        <span 
                          key={index} 
                          className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 text-red-600 text-xs rounded hover:bg-red-100 transition-colors"
                        >
                          {formatPlaceTypeDisplay(type)}
                          <button
                            onClick={() => removeExcludedType(index)}
                            className="text-red-600 hover:text-red-800 font-bold leading-none"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <PlaceTypeDropdown
                    placeholder="Add type..."
                    onAdd={addExcludedType}
                    existingTypes={excludedTypes}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search Button - Fixed at bottom */}
      <div className="px-3 py-3 bg-white border-t border-gray-300">
        {(shapeSearchMode === 'polygon' || shapeSearchMode === 'tube') ? (
          <button
            onClick={handlePolygonSearch}
            disabled={isSearching || selectedPolygonCircles.size === 0 || (filterSearchMode === 'category' && includedTypes.length === 0) || (filterSearchMode === 'text' && !textQuery.trim()) || getRemainingRequests() < selectedPolygonCircles.size}
            className="w-full px-3 py-2.5 bg-blue-500 text-white rounded text-xs font-semibold hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-sm"
          >
            {isSearching ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent mr-2"></div>
                Searching...
              </>
            ) : (
              `🔍 Search ${selectedPolygonCircles.size} Circle${selectedPolygonCircles.size !== 1 ? 's' : ''}`
            )}
          </button>
        ) : (
          <button
            onClick={handleSearch}
            disabled={isSearching || unsearchedAreas.length === 0 || unsearchedAreas.length > getAreaLimit() || (filterSearchMode === 'category' && includedTypes.length === 0) || (filterSearchMode === 'text' && !textQuery.trim()) || getRemainingRequests() < unsearchedAreas.length}
            className="w-full px-3 py-2.5 bg-blue-500 text-white rounded text-xs font-semibold hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-sm"
          >
            {isSearching ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent mr-2"></div>
                Searching...
              </>
            ) : (
              `🔍 Search ${unsearchedAreas.length} Area${unsearchedAreas.length !== 1 ? 's' : ''}`
            )}
          </button>
        )}
        
        {/* Error Messages */}
        {shapeSearchMode === 'circles' && unsearchedAreas.length > getRemainingRequests() && (
          <div className="text-xs text-red-600 mt-2 text-center font-medium">
            ⚠️ Need {unsearchedAreas.length} requests, have {getRemainingRequests()}
          </div>
        )}
        {(shapeSearchMode === 'polygon' || shapeSearchMode === 'tube') && selectedPolygonCircles.size > getRemainingRequests() && (
          <div className="text-xs text-red-600 mt-2 text-center font-medium">
            ⚠️ Need {selectedPolygonCircles.size} requests, have {getRemainingRequests()}
          </div>
        )}
        
        <div className="text-xs text-gray-500 mt-2 text-center">
          {getRemainingRequests()} / {getRequestLimit()} requests remaining
        </div>
        
        {shapeSearchMode === 'circles' && unsearchedAreas.length > getAreaLimit() && (
          <div className="text-xs text-red-600 mt-2 text-center font-medium">
            ⚠️ Remove {unsearchedAreas.length - getAreaLimit()} areas (limit: {getAreaLimit()})
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchSidebar;